import { useState, useEffect, useMemo, useRef } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Plus,
  Minus,
  Cog,
  Link as LinkIcon,
  Trash2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Download,
  FileText,
  Copy,
  Layers,
  Pencil,
  Bell,
  CopyPlus,
  Box,
  Palette,
  X,
  GripVertical,
  CheckCircle
} from 'lucide-react';
import { format, addDays, isSameDay, parseISO, addMinutes, isWithinInterval, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const HOUR_WIDTH = 60;
const ROW_HEIGHT = 70;
const MACHINE_LABEL_WIDTH = 250;
// 48 hours for 2-day view
const HOURS_PER_DAY = 24;
const TOTAL_HOURS = 48;
const HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => i);
// Minimum pixels to move before considering it a drag (prevents accidental drag on click)
const DRAG_THRESHOLD = 5;

// Default working hours configuration
const DEFAULT_WORKING_HOURS = {
  morning_start: 8,
  morning_end: 12,
  afternoon_start: 14,
  afternoon_end: 18,
  work_days: [1, 2, 3, 4, 5] // Monday to Friday (0 = Sunday, 1 = Monday, etc.)
};

const actionColors = [
  { bg: 'bg-blue-500', hex: '#3B82F6' },
  { bg: 'bg-green-500', hex: '#22C55E' },
  { bg: 'bg-orange-500', hex: '#F97316' },
  { bg: 'bg-purple-500', hex: '#A855F7' },
  { bg: 'bg-pink-500', hex: '#EC4899' },
  { bg: 'bg-cyan-500', hex: '#06B6D4' },
  { bg: 'bg-yellow-500', hex: '#EAB308' },
  { bg: 'bg-red-500', hex: '#EF4444' }
];

// Couleurs disponibles pour les actions (personnalisables)
const availableActionColors = [
  { value: 'auto', label: 'Couleur automatique', bg: 'bg-gray-400', hex: '#9CA3AF' },
  { value: 'blue', label: 'Bleu', bg: 'bg-blue-500', hex: '#3B82F6' },
  { value: 'green', label: 'Vert', bg: 'bg-green-500', hex: '#22C55E' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500', hex: '#F97316' },
  { value: 'purple', label: 'Violet', bg: 'bg-purple-500', hex: '#A855F7' },
  { value: 'pink', label: 'Rose', bg: 'bg-pink-500', hex: '#EC4899' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500', hex: '#06B6D4' },
  { value: 'yellow', label: 'Jaune', bg: 'bg-yellow-500', hex: '#EAB308' },
  { value: 'red', label: 'Rouge', bg: 'bg-red-500', hex: '#EF4444' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500', hex: '#6366F1' },
  { value: 'teal', label: 'Sarcelle', bg: 'bg-teal-500', hex: '#14B8A6' },
];

// Couleurs pour les lignes de machines
const rowColors = [
  { name: 'Aucune', value: '', class: '' },
  { name: 'Rouge', value: 'red', class: 'bg-red-100 dark:bg-red-950' },
  { name: 'Orange', value: 'orange', class: 'bg-orange-100 dark:bg-orange-950' },
  { name: 'Jaune', value: 'yellow', class: 'bg-yellow-100 dark:bg-yellow-950' },
  { name: 'Vert', value: 'green', class: 'bg-green-100 dark:bg-green-950' },
  { name: 'Bleu', value: 'blue', class: 'bg-blue-100 dark:bg-blue-950' },
  { name: 'Violet', value: 'purple', class: 'bg-purple-100 dark:bg-purple-950' },
  { name: 'Gris', value: 'gray', class: 'bg-gray-200 dark:bg-gray-800' },
];

const liaisonTypeColors = {
  sequence: '#3B82F6',
  dependency: '#F97316',
  parallel: '#22C55E'
};

// Helper function to get action color
const getActionColor = (action, actionIndex) => {
  if (action.color && action.color !== 'auto') {
    // Use custom color if set
    const customColor = availableActionColors.find(c => c.value === action.color);
    return customColor || actionColors[actionIndex % actionColors.length];
  }
  // Default: cycle through actionColors based on index
  return actionColors[actionIndex % actionColors.length];
};

export default function PlanningPage() {
  const [ilots, setIlots] = useState([]);
  const [machines, setMachines] = useState([]);
  const [actions, setActions] = useState([]);
  const [liaisons, setLiaisons] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [liaisonDialogOpen, setLiaisonDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showLiaisons, setShowLiaisons] = useState(true);
  const [machineColors, setMachineColors] = useState({}); // machine_id -> color value

  // Liaison creation mode state
  const [liaisonCreationMode, setLiaisonCreationMode] = useState(false);
  const [liaisonSource, setLiaisonSource] = useState(null); // action_id of source
  const [liaisonTypeToCreate, setLiaisonTypeToCreate] = useState('sequence');

  // Working hours configuration
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);

  // Drag and drop state
  const [draggingAction, setDraggingAction] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreviewTime, setDragPreviewTime] = useState(null); // { start: Date, end: Date }
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 }); // Initial mouse position for threshold check

  const timelineRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const timelineGridRef = useRef(null);
  
  const [actionForm, setActionForm] = useState({
    machine_id: '',
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    line_order: 0,
    color: 'auto',
    display_title: true,
    display_description: false,
    display_time: true,
    // Production fields
    of_number: '',
    article: '',
    designation: '',
    client: '',
    order_number: '',
    quantity: 0,
    display_of: false,
    display_article: false,
    display_designation: false,
    display_client: false,
    display_order_number: false,
    display_quantity: false,
    // Operator tracking fields
    operator_date: '',
    operator_name: '',
    quantity_produced: 0,
    quantity_nc: 0,
    time_spent: 0,
    operator_notes: '',
    // Completion status
    is_completed: false
  });
  
  const [liaisonForm, setLiaisonForm] = useState({
    source_action_id: '',
    target_action_id: '',
    type: 'sequence'
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    description: '',
    duration_minutes: 60,
    default_machine_type: 'aucun'
  });

  useEffect(() => {
    fetchData();
    fetchWorkingHours();
  }, []);

  const fetchData = async () => {
    try {
      const [ilotsRes, machinesRes, actionsRes, liaisonsRes, templatesRes, operatorsRes] = await Promise.all([
        fetch(`${API}/ilots`, { credentials: 'include' }),
        fetch(`${API}/machines`, { credentials: 'include' }),
        fetch(`${API}/actions`, { credentials: 'include' }),
        fetch(`${API}/liaisons`, { credentials: 'include' }),
        fetch(`${API}/templates`, { credentials: 'include' }),
        fetch(`${API}/operators?active_only=true`, { credentials: 'include' })
      ]);

      if (ilotsRes.ok) setIlots(await ilotsRes.json());
      if (machinesRes.ok) setMachines(await machinesRes.json());
      if (actionsRes.ok) setActions(await actionsRes.json());
      if (liaisonsRes.ok) setLiaisons(await liaisonsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (operatorsRes.ok) setOperators(await operatorsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingHours = async () => {
    try {
      const response = await fetch(`${API}/working-hours`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setWorkingHours(data);
      }
    } catch (error) {
      console.error('Failed to fetch working hours:', error);
    }
  };

  // Group machines by ilot
  const machinesByIlot = useMemo(() => {
    const grouped = {};
    ilots.forEach(ilot => {
      grouped[ilot.ilot_id] = {
        ilot,
        machines: machines.filter(m => m.ilot_id === ilot.ilot_id)
      };
    });
    // Add machines without ilot
    const orphanMachines = machines.filter(m => !ilots.find(i => i.ilot_id === m.ilot_id));
    if (orphanMachines.length > 0) {
      grouped['no-ilot'] = {
        ilot: { ilot_id: 'no-ilot', name: 'Sans îlot', display_order: 999 },
        machines: orphanMachines
      };
    }
    return grouped;
  }, [ilots, machines]);

  // Flatten machines with ilot info for row rendering
  const flatMachineList = useMemo(() => {
    const list = [];
    Object.values(machinesByIlot)
      .sort((a, b) => a.ilot.display_order - b.ilot.display_order)
      .forEach(({ ilot, machines: ilotMachines }) => {
        if (ilotMachines.length > 0) {
          list.push({ type: 'ilot', ilot });
          ilotMachines.forEach(machine => {
            list.push({ type: 'machine', machine, ilot });
          });
        }
      });
    return list;
  }, [machinesByIlot]);

  // Filter actions for both days (48h view)
  const day1 = selectedDate;
  const day2 = addDays(selectedDate, 1);

  const filteredActions = useMemo(() => {
    return actions.filter(action => {
      const actionDate = parseISO(action.start_time);
      return isSameDay(actionDate, day1) || isSameDay(actionDate, day2);
    });
  }, [actions, day1, day2]);

  // Helper to check if an hour is within working hours
  const isWorkingHour = (hour, dayOffset = 0) => {
    const currentDay = addDays(selectedDate, dayOffset);
    const dayOfWeek = currentDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Check if it's a working day
    if (!workingHours.work_days.includes(dayOfWeek)) {
      return false;
    }

    // Check morning hours
    if (hour >= workingHours.morning_start && hour < workingHours.morning_end) {
      return true;
    }
    // Check afternoon hours
    if (hour >= workingHours.afternoon_start && hour < workingHours.afternoon_end) {
      return true;
    }
    return false;
  };

  // Map actions to their positions for SVG arrows (accounting for ilot headers)
  const actionPositions = useMemo(() => {
    const positions = {};
    filteredActions.forEach((action, index) => {
      // Find the row index in flatMachineList
      const rowIndex = flatMachineList.findIndex(
        item => item.type === 'machine' && item.machine.machine_id === action.machine_id
      );
      if (rowIndex === -1) return;

      const start = parseISO(action.start_time);
      const end = parseISO(action.end_time);
      const isDay2 = isSameDay(start, day2);
      const dayOffset = isDay2 ? HOURS_PER_DAY : 0;

      const startHour = start.getHours() + start.getMinutes() / 60 + dayOffset;
      const endHour = end.getHours() + end.getMinutes() / 60 + dayOffset;

      // Position relative to timeline grid (not including label width for SVG)
      positions[action.action_id] = {
        x: startHour * HOUR_WIDTH,
        width: (endHour - startHour) * HOUR_WIDTH,
        y: rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2,
        color: actionColors[index % actionColors.length].hex
      };
    });
    return positions;
  }, [filteredActions, flatMachineList, day2]);

  // Initialize machine colors from loaded data
  useEffect(() => {
    const colors = {};
    machines.forEach(m => {
      if (m.row_color) {
        colors[m.machine_id] = m.row_color;
      }
    });
    setMachineColors(colors);
  }, [machines]);

  // Handle machine row color change - save to API
  const handleMachineColorChange = async (machineId, color) => {
    // Update local state immediately for responsiveness
    setMachineColors(prev => ({
      ...prev,
      [machineId]: color
    }));
    
    // Save to backend
    try {
      const response = await fetch(`${API}/machines/${machineId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ row_color: color || null })
      });
      
      if (response.ok) {
        toast.success('Couleur enregistrée');
      }
    } catch (error) {
      console.error('Failed to save color:', error);
    }
  };

  const getRowColorClass = (machineId) => {
    const colorValue = machineColors[machineId];
    if (!colorValue) return '';
    const colorObj = rowColors.find(c => c.value === colorValue);
    return colorObj?.class || '';
  };

  // Filter liaisons to show only those between visible actions
  const visibleLiaisons = useMemo(() => {
    const visibleActionIds = new Set(filteredActions.map(a => a.action_id));
    return liaisons.filter(l => 
      visibleActionIds.has(l.source_action_id) && visibleActionIds.has(l.target_action_id)
    );
  }, [liaisons, filteredActions]);

  const handleOpenCreateAction = (template = null) => {
    if (machines.length === 0) {
      toast.error('Créez d\'abord une machine');
      return;
    }
    setEditingAction(null);
    const startTime = new Date(selectedDate);
    startTime.setHours(8, 0, 0, 0);
    const duration = template?.duration_minutes || 60;
    const endTime = addMinutes(startTime, duration);
    
    let defaultMachineId = machines[0].machine_id;
    if (template?.default_machine_type && template.default_machine_type !== 'aucun') {
      const matchingMachine = machines.find(m => m.type === template.default_machine_type);
      if (matchingMachine) defaultMachineId = matchingMachine.machine_id;
    }
    
    setActionForm({
      machine_id: defaultMachineId,
      title: template?.title || '',
      description: template?.description || '',
      start_time: startTime.toISOString().slice(0, 16),
      end_time: endTime.toISOString().slice(0, 16),
      line_order: 0,
      color: 'auto',
      display_title: true,
      display_description: false,
      display_time: true,
      // Production fields
      of_number: '',
      article: '',
      designation: '',
      client: '',
      order_number: '',
      quantity: 0,
      display_of: false,
      display_article: false,
      display_designation: false,
      display_client: false,
      display_order_number: false,
      display_quantity: false,
      // Operator tracking fields
      operator_date: '',
      operator_name: '',
      quantity_produced: 0,
      quantity_nc: 0,
      time_spent: 0,
      operator_notes: '',
      // Completion status
      is_completed: false
    });
    setActionDialogOpen(true);
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setActionForm({
      machine_id: action.machine_id,
      title: action.title,
      description: action.description,
      start_time: action.start_time.slice(0, 16),
      end_time: action.end_time.slice(0, 16),
      line_order: action.line_order,
      color: action.color || 'auto',
      display_title: action.display_title !== false,
      display_description: action.display_description || false,
      display_time: action.display_time !== false,
      // Production fields
      of_number: action.of_number || '',
      article: action.article || '',
      designation: action.designation || '',
      client: action.client || '',
      order_number: action.order_number || '',
      quantity: action.quantity || 0,
      display_of: action.display_of || false,
      display_article: action.display_article || false,
      display_designation: action.display_designation || false,
      display_client: action.display_client || false,
      display_order_number: action.display_order_number || false,
      display_quantity: action.display_quantity || false,
      // Operator tracking fields - default to now if not set
      operator_date: action.operator_date ? action.operator_date.slice(0, 16) : new Date().toISOString().slice(0, 16),
      operator_name: action.operator_name || '',
      quantity_produced: action.quantity_produced || 0,
      quantity_nc: action.quantity_nc || 0,
      time_spent: action.time_spent || 0,
      operator_notes: action.operator_notes || '',
      // Completion status
      is_completed: action.is_completed || false
    });
    setActionDialogOpen(true);
  };

  // Helper to convert datetime-local value to ISO string preserving local time
  const toLocalISOString = (dateTimeLocal) => {
    if (!dateTimeLocal) return null;
    // datetime-local format: "2024-01-31T08:00"
    // We need to add seconds and timezone to make it a valid ISO string
    // The backend will interpret this as the intended local time
    const date = new Date(dateTimeLocal);
    const tzOffset = -date.getTimezoneOffset();
    const sign = tzOffset >= 0 ? '+' : '-';
    const hours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
    const minutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
    return `${dateTimeLocal}:00${sign}${hours}:${minutes}`;
  };

  // Helper to convert Date object to ISO string preserving local time
  const dateToLocalISOString = (date) => {
    if (!date) return null;
    const tzOffset = -date.getTimezoneOffset();
    const sign = tzOffset >= 0 ? '+' : '-';
    const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
    const tzMinutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${tzHours}:${tzMinutes}`;
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();

    try {
      const url = editingAction ? `${API}/actions/${editingAction.action_id}` : `${API}/actions`;
      const method = editingAction ? 'PUT' : 'POST';

      // Convert 'auto' to empty string for backend
      const dataToSend = {
        machine_id: actionForm.machine_id,
        title: actionForm.title,
        description: actionForm.description,
        start_time: toLocalISOString(actionForm.start_time),
        end_time: toLocalISOString(actionForm.end_time),
        line_order: actionForm.line_order,
        color: actionForm.color === 'auto' ? '' : actionForm.color,
        display_title: actionForm.display_title,
        display_description: actionForm.display_description,
        display_time: actionForm.display_time,
        // Production fields
        of_number: actionForm.of_number,
        article: actionForm.article,
        designation: actionForm.designation,
        client: actionForm.client,
        order_number: actionForm.order_number,
        quantity: actionForm.quantity,
        display_of: actionForm.display_of,
        display_article: actionForm.display_article,
        display_designation: actionForm.display_designation,
        display_client: actionForm.display_client,
        display_order_number: actionForm.display_order_number,
        display_quantity: actionForm.display_quantity,
        // Operator tracking fields
        operator_date: actionForm.operator_date ? toLocalISOString(actionForm.operator_date) : null,
        operator_name: actionForm.operator_name,
        quantity_produced: actionForm.quantity_produced,
        quantity_nc: actionForm.quantity_nc,
        time_spent: actionForm.time_spent,
        operator_notes: actionForm.operator_notes,
        // Completion status
        is_completed: actionForm.is_completed
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        toast.success(editingAction ? 'Action mise à jour' : 'Action créée');
        setActionDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDeleteAction = async () => {
    if (!editingAction) return;
    if (!window.confirm('Supprimer cette action ?')) return;
    
    try {
      const response = await fetch(`${API}/actions/${editingAction.action_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Action supprimée');
        setActionDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleGenerateDescription = async () => {
    if (!actionForm.title || !actionForm.machine_id) {
      toast.error('Remplissez le titre et la machine');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const machine = machines.find(m => m.machine_id === actionForm.machine_id);
      const response = await fetch(`${API}/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action_title: actionForm.title,
          machine_type: machine?.type || 'autre',
          context: ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        setActionForm({ ...actionForm, description: data.description });
        toast.success('Description générée');
      } else {
        toast.error('Erreur de génération');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Duplicate action
  const handleDuplicateAction = async (actionId) => {
    try {
      const response = await fetch(`${API}/actions/${actionId}/duplicate?offset_minutes=60`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Action dupliquée (+1h)');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de duplication');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  // Generate notifications for action
  const handleGenerateNotifications = async (actionId) => {
    try {
      const response = await fetch(`${API}/actions/${actionId}/generate-notifications?reminder_minutes=15`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.notifications.length} notification(s) créée(s)`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleOpenCreateLiaison = () => {
    if (filteredActions.length < 2) {
      toast.error('Il faut au moins 2 actions ce jour pour créer une liaison');
      return;
    }
    setLiaisonForm({
      source_action_id: filteredActions[0].action_id,
      target_action_id: filteredActions[1].action_id,
      type: 'sequence'
    });
    setLiaisonDialogOpen(true);
  };

  const handleSubmitLiaison = async (e) => {
    e.preventDefault();
    
    if (liaisonForm.source_action_id === liaisonForm.target_action_id) {
      toast.error('Les actions source et cible doivent être différentes');
      return;
    }
    
    try {
      const response = await fetch(`${API}/liaisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(liaisonForm)
      });

      if (response.ok) {
        toast.success('Liaison créée');
        setLiaisonDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDeleteLiaison = async (liaisonId) => {
    try {
      const response = await fetch(`${API}/liaisons/${liaisonId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Liaison supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Liaison creation via click handlers
  const handleStartLiaison = (actionId, e) => {
    e.stopPropagation();
    setLiaisonSource(actionId);
    setLiaisonCreationMode(true);
    toast.info('Cliquez sur le "-" d\'une autre action pour créer la liaison', { duration: 3000 });
  };

  const handleCompleteLiaison = async (targetActionId, e) => {
    e.stopPropagation();

    if (!liaisonSource) {
      toast.error('Cliquez d\'abord sur le "+" d\'une action source');
      return;
    }

    if (liaisonSource === targetActionId) {
      toast.error('Les actions source et cible doivent être différentes');
      cancelLiaisonCreation();
      return;
    }

    try {
      const response = await fetch(`${API}/liaisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_action_id: liaisonSource,
          target_action_id: targetActionId,
          type: liaisonTypeToCreate
        })
      });

      if (response.ok) {
        toast.success('Liaison créée');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      cancelLiaisonCreation();
    }
  };

  const cancelLiaisonCreation = () => {
    setLiaisonSource(null);
    setLiaisonCreationMode(false);
  };

  // Drag and drop handlers
  const handleDragStart = (action, e) => {
    if (liaisonCreationMode) return;
    e.preventDefault();
    e.stopPropagation();

    const actionElement = e.currentTarget;
    const rect = actionElement.getBoundingClientRect();

    setDraggingAction(action);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setDragPosition({
      x: rect.left,
      y: rect.top
    });
    // Store initial mouse position for threshold check
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setIsDragging(true);
    document.body.classList.add('is-dragging');
  };

  const handleDragMove = (e) => {
    if (!isDragging || !draggingAction) return;

    setDragPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });

    // Calculate preview time based on mouse position
    const timelineBody = timelineRef.current?.querySelector('.relative.bg-white');
    if (timelineBody) {
      const timelineRect = timelineBody.getBoundingClientRect();
      const scrollLeft = timelineRef.current?.scrollLeft || 0;

      const dropX = e.clientX - timelineRect.left + scrollLeft - MACHINE_LABEL_WIDTH;
      const hourOffset = dropX / HOUR_WIDTH;
      const dayOffset = hourOffset >= HOURS_PER_DAY ? 1 : 0;
      const hourInDay = hourOffset % HOURS_PER_DAY;

      // Calculate duration of original action
      const originalStart = parseISO(draggingAction.start_time);
      const originalEnd = parseISO(draggingAction.end_time);
      const durationMs = originalEnd.getTime() - originalStart.getTime();

      // Calculate new start time (snap to 15min)
      const baseDate = addDays(selectedDate, dayOffset);
      const newStartHour = Math.floor(hourInDay);
      const newStartMinutes = Math.round((hourInDay - newStartHour) * 60 / 15) * 15;

      const previewStart = new Date(baseDate);
      previewStart.setHours(newStartHour, newStartMinutes, 0, 0);
      const previewEnd = new Date(previewStart.getTime() + durationMs);

      setDragPreviewTime({ start: previewStart, end: previewEnd });
    }
  };

  const handleDragEnd = async (e) => {
    document.body.classList.remove('is-dragging');

    if (!isDragging || !draggingAction) {
      setIsDragging(false);
      setDraggingAction(null);
      setDragPreviewTime(null);
      return;
    }

    // Check if mouse moved beyond threshold (to distinguish click from drag)
    const deltaX = Math.abs(e.clientX - dragStartPos.x);
    const deltaY = Math.abs(e.clientY - dragStartPos.y);
    if (deltaX < DRAG_THRESHOLD && deltaY < DRAG_THRESHOLD) {
      // It was a click, not a drag - don't update position
      setIsDragging(false);
      setDraggingAction(null);
      setDragPreviewTime(null);
      return;
    }

    // Get timeline grid position
    const timelineBody = timelineRef.current?.querySelector('.relative.bg-white');
    if (!timelineBody) {
      setIsDragging(false);
      setDraggingAction(null);
      return;
    }

    const timelineRect = timelineBody.getBoundingClientRect();
    const scrollLeft = timelineRef.current?.scrollLeft || 0;

    // Calculate drop position relative to timeline
    const dropX = e.clientX - timelineRect.left + scrollLeft - MACHINE_LABEL_WIDTH;
    const dropY = e.clientY - timelineRect.top;

    // Calculate new hour based on X position
    const hourOffset = dropX / HOUR_WIDTH;
    const dayOffset = hourOffset >= HOURS_PER_DAY ? 1 : 0;
    const hourInDay = hourOffset % HOURS_PER_DAY;

    // Calculate new machine based on Y position
    let newMachineId = draggingAction.machine_id;
    let accumulatedHeight = 0;
    for (const item of flatMachineList) {
      if (item.type === 'machine') {
        if (dropY >= accumulatedHeight && dropY < accumulatedHeight + ROW_HEIGHT) {
          newMachineId = item.machine.machine_id;
          break;
        }
      }
      accumulatedHeight += ROW_HEIGHT;
    }

    // Calculate duration of original action
    const originalStart = parseISO(draggingAction.start_time);
    const originalEnd = parseISO(draggingAction.end_time);
    const durationMs = originalEnd.getTime() - originalStart.getTime();

    // Calculate new start time
    const baseDate = addDays(selectedDate, dayOffset);
    const newStartHour = Math.floor(hourInDay);
    const newStartMinutes = Math.round((hourInDay - newStartHour) * 60 / 15) * 15; // Snap to 15min

    const newStart = new Date(baseDate);
    newStart.setHours(newStartHour, newStartMinutes, 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);

    // Only update if position actually changed
    const originalStartTime = new Date(draggingAction.start_time).getTime();
    if (newStart.getTime() !== originalStartTime || newMachineId !== draggingAction.machine_id) {
      try {
        const response = await fetch(`${API}/actions/${draggingAction.action_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            machine_id: newMachineId,
            start_time: dateToLocalISOString(newStart),
            end_time: dateToLocalISOString(newEnd)
          })
        });

        if (response.ok) {
          toast.success(`Action déplacée: ${format(newStart, 'HH:mm', { locale: fr })} - ${format(newEnd, 'HH:mm', { locale: fr })}`);
          fetchData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erreur lors du déplacement');
        }
      } catch (error) {
        toast.error('Erreur réseau');
      }
    }

    setIsDragging(false);
    setDraggingAction(null);
    setDragPreviewTime(null);
  };

  // Add global mouse move and up listeners when dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = (e) => handleDragEnd(e);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, draggingAction, dragOffset, dragStartPos]);

  // Template handlers
  const handleOpenCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      title: '',
      description: '',
      duration_minutes: 60,
      default_machine_type: 'aucun'
    });
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      title: template.title,
      description: template.description,
      duration_minutes: template.duration_minutes,
      default_machine_type: template.default_machine_type || 'aucun'
    });
    setTemplateDialogOpen(true);
  };

  const handleSubmitTemplate = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTemplate ? `${API}/templates/${editingTemplate.template_id}` : `${API}/templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template mis à jour' : 'Template créé');
        setTemplateDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!editingTemplate) return;
    if (!window.confirm('Supprimer ce template ?')) return;
    
    try {
      const response = await fetch(`${API}/templates/${editingTemplate.template_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Template supprimé');
        setTemplateDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (!timelineRef.current) return;
    
    setExportingPdf(true);
    toast.info('Génération du PDF en cours...');
    
    try {
      const canvas = await html2canvas(timelineRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add title
      pdf.setFontSize(18);
      pdf.setTextColor(196, 30, 58); // Taptools red
      pdf.text('TAP-CN - Planning', 14, 15);
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Date: ${format(selectedDate, 'dd MMMM yyyy', { locale: fr })}`, 14, 22);
      
      // Add timeline image
      const imgWidth = pageWidth - 28;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const maxImgHeight = pageHeight - 35;
      
      const finalHeight = Math.min(imgHeight, maxImgHeight);
      const finalWidth = (finalHeight === maxImgHeight) ? (canvas.width * maxImgHeight) / canvas.height : imgWidth;
      
      pdf.addImage(imgData, 'PNG', 14, 28, finalWidth, finalHeight);
      
      // Add footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}`, 14, pageHeight - 5);
      pdf.text('TAP-CN Industrial OS - taptools.fr', pageWidth - 60, pageHeight - 5);
      
      pdf.save(`planning-${format(selectedDate, 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exporté avec succès');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const getActionPosition = (action) => {
    const start = parseISO(action.start_time);
    const end = parseISO(action.end_time);
    const isDay2 = isSameDay(start, day2);
    const dayOffset = isDay2 ? HOURS_PER_DAY : 0;

    const startHour = start.getHours() + start.getMinutes() / 60 + dayOffset;
    const endHour = end.getHours() + end.getMinutes() / 60 + dayOffset;
    const duration = endHour - startHour;

    return {
      left: startHour * HOUR_WIDTH,
      width: Math.max(duration * HOUR_WIDTH, 30)
    };
  };

  const getMachineName = (machineId) => {
    const machine = machines.find(m => m.machine_id === machineId);
    return machine?.name || 'N/A';
  };

  const getActionTitle = (actionId) => {
    const action = actions.find(a => a.action_id === actionId);
    return action?.title || 'N/A';
  };

  // Generate SVG path for liaison arrow
  const generateArrowPath = (liaison) => {
    const sourcePos = actionPositions[liaison.source_action_id];
    const targetPos = actionPositions[liaison.target_action_id];
    
    if (!sourcePos || !targetPos) return null;
    
    const sourceX = sourcePos.x + sourcePos.width;
    const sourceY = sourcePos.y;
    const targetX = targetPos.x;
    const targetY = targetPos.y;
    
    // Control points for curved line
    const midX = (sourceX + targetX) / 2;
    const curveOffset = Math.abs(targetY - sourceY) / 4;
    
    const path = `M ${sourceX} ${sourceY} 
                  C ${midX + curveOffset} ${sourceY}, 
                    ${midX - curveOffset} ${targetY}, 
                    ${targetX - 8} ${targetY}`;
    
    return { path, targetX: targetX - 8, targetY, color: liaisonTypeColors[liaison.type] || '#6B7280' };
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="planning-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Planification
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Planifiez les actions et créez des liaisons
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-sm border-2 gap-2" data-testid="date-picker-btn">
                <CalendarIcon className="w-4 h-4" />
                {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={fr}
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" onClick={() => setSelectedDate(addDays(selectedDate, -1))} className="rounded-sm border-2">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => setSelectedDate(new Date())} className="rounded-sm border-2">
            Aujourd'hui
          </Button>
          <Button variant="outline" onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="rounded-sm border-2">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => handleOpenCreateAction()} className="btn-industrial rounded-sm" data-testid="create-action-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Action
        </Button>
        <Button onClick={handleOpenCreateLiaison} variant="outline" className="rounded-sm border-2" data-testid="create-liaison-btn">
          <LinkIcon className="w-4 h-4 mr-2" />
          Nouvelle Liaison
        </Button>
        <Button onClick={handleOpenCreateTemplate} variant="outline" className="rounded-sm border-2" data-testid="create-template-btn">
          <Layers className="w-4 h-4 mr-2" />
          Nouveau Template
        </Button>
        <Button 
          onClick={handleExportPDF} 
          variant="outline" 
          className="rounded-sm border-2"
          disabled={exportingPdf}
          data-testid="export-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {exportingPdf ? 'Export...' : 'Exporter PDF'}
        </Button>
        <Button
          onClick={() => setShowLiaisons(!showLiaisons)}
          variant={showLiaisons ? "default" : "outline"}
          className="rounded-sm border-2"
          data-testid="toggle-liaisons-btn"
        >
          <LinkIcon className="w-4 h-4 mr-2" />
          {showLiaisons ? 'Masquer' : 'Afficher'} Liaisons
        </Button>
      </div>

      {/* Liaison Creation Mode Indicator */}
      {liaisonCreationMode && (
        <Card className="border-2 border-green-500 rounded-sm bg-green-50 dark:bg-green-950">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <LinkIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-sm text-green-800 dark:text-green-200">
                    Mode création de liaison actif
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Cliquez sur le bouton <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500 text-white text-[10px]">−</span> d'une action pour créer la liaison
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={liaisonTypeToCreate} onValueChange={setLiaisonTypeToCreate}>
                  <SelectTrigger className="w-36 h-8 rounded-sm border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sequence">Séquence</SelectItem>
                    <SelectItem value="dependency">Dépendance</SelectItem>
                    <SelectItem value="parallel">Parallèle</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelLiaisonCreation}
                  className="rounded-sm border-2 h-8"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Quick Access */}
      {templates.length > 0 && (
        <Card className="border-2 rounded-sm">
          <CardHeader className="pb-2 border-b border-border">
            <CardTitle className="font-heading font-semibold text-sm uppercase tracking-tight flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Templates Rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="flex flex-wrap gap-2">
              {templates.map((template) => (
                <Button
                  key={template.template_id}
                  variant="outline"
                  size="sm"
                  className="rounded-sm border gap-2 h-8"
                  onClick={() => handleOpenCreateAction(template)}
                  data-testid={`use-template-${template.template_id}`}
                >
                  <Copy className="w-3 h-3" />
                  {template.name}
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1">
                    {template.duration_minutes}min
                  </Badge>
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm h-8 text-muted-foreground"
                onClick={handleOpenCreateTemplate}
              >
                <Plus className="w-3 h-3 mr-1" />
                Ajouter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card className="border-2 rounded-sm">
        <CardContent className="p-0 overflow-visible">
          {machines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Cog className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
                Aucune machine
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                Créez des machines pour planifier des actions
              </p>
            </div>
          ) : (
            <div className="relative overflow-x-auto overflow-y-visible py-1" ref={timelineRef}>
              {/* Fixed Header + Scrollable Content */}
              <div className="overflow-visible" style={{ minWidth: MACHINE_LABEL_WIDTH + (HOURS.length * HOUR_WIDTH) }}>
                {/* Day Headers */}
                <div className="flex border-b border-border bg-card sticky top-0 z-20">
                  <div
                    className="border-r-2 border-border bg-card p-2 flex items-center sticky left-0 z-30"
                    style={{ width: MACHINE_LABEL_WIDTH, minWidth: MACHINE_LABEL_WIDTH }}
                  >
                    <span className="industrial-label text-xs">Vue 48h</span>
                  </div>
                  {/* Day 1 */}
                  <div
                    className="border-r-2 border-primary/30 text-center py-2 bg-primary/5"
                    style={{ width: HOURS_PER_DAY * HOUR_WIDTH }}
                  >
                    <span className="font-heading font-semibold text-sm uppercase tracking-tight text-primary">
                      {format(day1, 'EEEE d MMMM', { locale: fr })}
                    </span>
                  </div>
                  {/* Day 2 */}
                  <div
                    className="text-center py-2 bg-secondary/50"
                    style={{ width: HOURS_PER_DAY * HOUR_WIDTH }}
                  >
                    <span className="font-heading font-semibold text-sm uppercase tracking-tight text-muted-foreground">
                      {format(day2, 'EEEE d MMMM', { locale: fr })}
                    </span>
                  </div>
                </div>

                {/* Timeline Header with Hours */}
                <div className="flex border-b-2 border-border bg-card sticky top-8 z-20">
                  {/* Sticky Label Header */}
                  <div
                    className="border-r-2 border-border bg-card p-3 flex items-center sticky left-0 z-30"
                    style={{ width: MACHINE_LABEL_WIDTH, minWidth: MACHINE_LABEL_WIDTH }}
                  >
                    <span className="industrial-label">Îlots / Machines</span>
                  </div>
                  {/* Hours Header */}
                  <div className="flex">
                    {HOURS.map((hour) => {
                      const dayOffset = hour < HOURS_PER_DAY ? 0 : 1;
                      const displayHour = hour % HOURS_PER_DAY;
                      const isWorking = isWorkingHour(displayHour, dayOffset);
                      const isDayBoundary = hour === HOURS_PER_DAY;

                      return (
                        <div
                          key={hour}
                          className={`border-r text-center flex-shrink-0 transition-colors relative ${
                            isDayBoundary ? 'border-r-2 border-r-primary/30' : 'border-border'
                          } ${isWorking ? 'bg-green-100 dark:bg-green-950/50' : 'bg-gray-100 dark:bg-gray-900/50'}`}
                          style={{ width: HOUR_WIDTH }}
                        >
                          <div className="py-2">
                            <span className={`font-mono text-xs ${isWorking ? 'text-green-700 dark:text-green-400 font-bold' : 'text-muted-foreground'}`}>
                              {displayHour.toString().padStart(2, '0')}:00
                            </span>
                          </div>
                          {/* Colored bar indicator */}
                          <div
                            className={`absolute bottom-0 left-0 right-0 h-1.5 ${
                              isWorking
                                ? 'bg-green-500 dark:bg-green-500'
                                : 'bg-gray-300 dark:bg-gray-700'
                            }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Timeline Body with SVG overlay */}
                <div className="relative bg-white overflow-visible">
                  {/* SVG Layer for Liaisons */}
                  {showLiaisons && visibleLiaisons.length > 0 && (
                    <svg 
                      className="absolute top-0 pointer-events-none z-10"
                      style={{ 
                        height: flatMachineList.length * ROW_HEIGHT,
                        left: MACHINE_LABEL_WIDTH,
                        width: HOURS.length * HOUR_WIDTH
                      }}
                    >
                      <defs>
                        <marker id="arrowhead-sequence" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                          <polygon points="0 0, 10 3.5, 0 7" fill={liaisonTypeColors.sequence} />
                        </marker>
                        <marker id="arrowhead-dependency" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                          <polygon points="0 0, 10 3.5, 0 7" fill={liaisonTypeColors.dependency} />
                        </marker>
                        <marker id="arrowhead-parallel" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                          <polygon points="0 0, 10 3.5, 0 7" fill={liaisonTypeColors.parallel} />
                        </marker>
                      </defs>
                      {visibleLiaisons.map((liaison) => {
                        const arrowData = generateArrowPath(liaison);
                        if (!arrowData) return null;
                        return (
                          <path
                            key={liaison.liaison_id}
                            d={arrowData.path}
                            stroke={arrowData.color}
                            strokeWidth="2"
                            fill="none"
                            markerEnd={`url(#arrowhead-${liaison.type})`}
                            strokeDasharray={liaison.type === 'parallel' ? '5,5' : 'none'}
                            data-testid={`liaison-arrow-${liaison.liaison_id}`}
                          />
                        );
                      })}
                    </svg>
                  )}

                  {/* Timeline Rows - Grouped by Ilot */}
                  {flatMachineList.map((item, rowIndex) => {
                    if (item.type === 'ilot') {
                      // Ilot Header Row
                      return (
                        <div 
                          key={`ilot-${item.ilot.ilot_id}`} 
                          className="flex border-b-2 border-primary/30"
                          style={{ height: ROW_HEIGHT }}
                          data-testid={`ilot-row-${item.ilot.ilot_id}`}
                        >
                          {/* Sticky Ilot Label */}
                          <div 
                            className="border-r-2 border-border bg-primary/10 flex items-center gap-2 px-3 sticky left-0 z-20"
                            style={{ width: MACHINE_LABEL_WIDTH, minWidth: MACHINE_LABEL_WIDTH }}
                          >
                            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
                              <span className="text-primary-foreground font-bold text-sm">
                                {item.ilot.display_order + 1}
                              </span>
                            </div>
                            <div>
                              <span className="font-heading font-semibold text-sm uppercase tracking-tight">
                                {item.ilot.name}
                              </span>
                              <p className="text-[10px] text-muted-foreground font-mono">
                                {machinesByIlot[item.ilot.ilot_id]?.machines.length || 0} machine(s)
                              </p>
                            </div>
                          </div>
                          {/* Empty timeline area for ilot header */}
                          <div className="flex-1 bg-primary/5" style={{ minWidth: HOURS.length * HOUR_WIDTH }} />
                        </div>
                      );
                    }
                    
                    // Machine Row
                    const machine = item.machine;
                    const machineActions = filteredActions.filter(a => a.machine_id === machine.machine_id);
                    const rowColorClass = getRowColorClass(machine.machine_id);
                    
                    return (
                      <div
                        key={machine.machine_id}
                        className="flex border-b border-border overflow-visible"
                        style={{ height: ROW_HEIGHT }}
                        data-testid={`timeline-row-${machine.machine_id}`}
                      >
                        {/* Sticky Machine Label */}
                        <div 
                          className={`border-r-2 border-border flex items-center gap-2 px-3 sticky left-0 z-20 ${rowColorClass || 'bg-card'}`}
                          style={{ width: MACHINE_LABEL_WIDTH, minWidth: MACHINE_LABEL_WIDTH }}
                        >
                          <div className="w-6 h-6 rounded-sm bg-secondary flex items-center justify-center ml-4">
                            <Cog className="w-3 h-3" strokeWidth={1.5} />
                          </div>
                          <span className="font-medium text-sm truncate flex-1">{machine.name}</span>
                          {/* Color Picker */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button 
                                className="w-5 h-5 rounded border border-border hover:border-primary transition-colors flex items-center justify-center"
                                title="Coloriser la ligne"
                                data-testid={`color-picker-${machine.machine_id}`}
                              >
                                {machineColors[machine.machine_id] ? (
                                  <div className={`w-3 h-3 rounded-sm ${rowColors.find(c => c.value === machineColors[machine.machine_id])?.class || ''}`} />
                                ) : (
                                  <div className="w-3 h-3 rounded-sm bg-gray-200 border border-dashed border-gray-400" />
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2" align="start">
                              <div className="grid grid-cols-4 gap-1">
                                {rowColors.map((color) => (
                                  <button
                                    key={color.value || 'none'}
                                    onClick={() => handleMachineColorChange(machine.machine_id, color.value)}
                                    className={`w-6 h-6 rounded border transition-all ${
                                      machineColors[machine.machine_id] === color.value 
                                        ? 'border-primary ring-2 ring-primary/30' 
                                        : 'border-border hover:border-primary'
                                    } ${color.class || 'bg-white'}`}
                                    title={color.name}
                                  />
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        {/* Timeline Grid */}
                        <div
                          className={`relative timeline-grid flex overflow-visible ${rowColorClass}`}
                          style={{ width: HOURS.length * HOUR_WIDTH, minWidth: HOURS.length * HOUR_WIDTH }}
                        >
                          {/* Hour columns with working hours highlighting */}
                          {HOURS.map((hour) => {
                            const dayOffset = hour < HOURS_PER_DAY ? 0 : 1;
                            const displayHour = hour % HOURS_PER_DAY;
                            const isWorking = isWorkingHour(displayHour, dayOffset);
                            const isDayBoundary = hour === HOURS_PER_DAY;

                            return (
                              <div
                                key={hour}
                                className={`absolute top-0 bottom-0 border-r ${
                                  isDayBoundary ? 'border-r-2 border-r-primary/30' : 'border-border/50'
                                } ${isWorking ? 'bg-green-50/50 dark:bg-green-950/20' : 'bg-gray-100/50 dark:bg-gray-900/30'}`}
                                style={{ left: hour * HOUR_WIDTH, width: HOUR_WIDTH }}
                              />
                            );
                          })}
                          {machineActions.map((action) => {
                            const pos = getActionPosition(action);
                            const actionIndex = filteredActions.indexOf(action);
                            const colorInfo = getActionColor(action, actionIndex);
                            const isLiaisonSource = liaisonSource === action.action_id;
                            const isBeingDragged = isDragging && draggingAction?.action_id === action.action_id;
                            const isCompleted = action.is_completed;
                            const hasImportantInfo = action.display_description && action.description;

                            const actionBlock = (
                              <div
                                key={action.action_id}
                                className={`action-block ${isCompleted ? 'bg-gray-400' : colorInfo.bg} text-white z-10 group relative ${isLiaisonSource ? 'ring-2 ring-white ring-offset-2 ring-offset-background' : ''} ${isBeingDragged ? 'opacity-30' : ''} ${isCompleted ? 'opacity-60' : ''} ${!liaisonCreationMode ? 'cursor-grab active:cursor-grabbing' : ''} ${hasImportantInfo ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}
                                style={{
                                  left: pos.left,
                                  width: pos.width,
                                  top: 4 + (action.line_order * 18),
                                  height: 'calc(100% - 8px)'
                                }}
                                onMouseDown={(e) => handleDragStart(action, e)}
                                data-testid={`action-block-${action.action_id}`}
                              >
                                {/* Liaison Target Button (-) on left */}
                                <button
                                  type="button"
                                  onClick={(e) => handleCompleteLiaison(action.action_id, e)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className={`absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md transition-all z-30 ${
                                    liaisonCreationMode && !isLiaisonSource
                                      ? 'opacity-100 scale-100 cursor-pointer hover:bg-orange-600 hover:scale-110'
                                      : 'opacity-0 group-hover:opacity-70 scale-75 group-hover:scale-100'
                                  }`}
                                  title="Liaison entrante (cible)"
                                  data-testid={`liaison-target-${action.action_id}`}
                                >
                                  <Minus className="w-3 h-3" strokeWidth={3} />
                                </button>

                                <div
                                  className="flex h-full px-1 py-1 cursor-pointer"
                                  onClick={() => !liaisonCreationMode && !isDragging && handleEditAction(action)}
                                >
                                  <GripVertical className="w-3 h-3 opacity-40 flex-shrink-0 mt-0.5" />
                                  {isCompleted && (
                                    <CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5 mr-1" />
                                  )}
                                  <div className="flex flex-col justify-center flex-1 px-1 overflow-hidden select-none">
                                    {action.display_title !== false && (
                                      <span className={`font-medium text-xs truncate leading-tight ${isCompleted ? 'line-through' : ''}`}>
                                        {action.title}
                                      </span>
                                    )}
                                    {action.display_time !== false && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        {format(parseISO(action.start_time), 'HH:mm', { locale: fr })} - {format(parseISO(action.end_time), 'HH:mm', { locale: fr })}
                                      </span>
                                    )}
                                    {action.display_description && action.description && (
                                      <span className="text-[10px] opacity-70 truncate leading-tight italic">
                                        {action.description.slice(0, 40)}{action.description.length > 40 ? '...' : ''}
                                      </span>
                                    )}
                                    {/* Production fields */}
                                    {action.display_of && action.of_number && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        OF: {action.of_number}
                                      </span>
                                    )}
                                    {action.display_article && action.article && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        Art: {action.article}
                                      </span>
                                    )}
                                    {action.display_designation && action.designation && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        {action.designation.slice(0, 30)}{action.designation.length > 30 ? '...' : ''}
                                      </span>
                                    )}
                                    {action.display_client && action.client && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        Client: {action.client}
                                      </span>
                                    )}
                                    {action.display_order_number && action.order_number && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        Cmd: {action.order_number}
                                      </span>
                                    )}
                                    {action.display_quantity && action.quantity > 0 && (
                                      <span className="text-[10px] opacity-80 truncate leading-tight">
                                        Qté: {action.quantity}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Liaison Source Button (+) on right */}
                                <button
                                  type="button"
                                  onClick={(e) => handleStartLiaison(action.action_id, e)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className={`absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center shadow-md transition-all z-30 ${
                                    isLiaisonSource
                                      ? 'opacity-100 scale-110 bg-green-600'
                                      : 'opacity-0 group-hover:opacity-70 scale-75 group-hover:scale-100 hover:bg-green-600 hover:scale-110'
                                  }`}
                                  title="Créer une liaison (source)"
                                  data-testid={`liaison-source-${action.action_id}`}
                                >
                                  <Plus className="w-3 h-3" strokeWidth={3} />
                                </button>

                                {/* Hover buttons */}
                                <div
                                  className="absolute right-6 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-black/40 rounded px-1 py-0.5 z-20"
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDuplicateAction(action.action_id); }}
                                    className="p-1 hover:bg-white/30 rounded transition-colors"
                                    title="Dupliquer (+1h)"
                                    data-testid={`duplicate-action-${action.action_id}`}
                                  >
                                    <CopyPlus className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditAction(action); }}
                                    className="p-1 hover:bg-white/30 rounded transition-colors"
                                    title="Modifier l'action"
                                    data-testid={`edit-action-${action.action_id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            );

                            // Wrap with Tooltip if there's important info
                            if (hasImportantInfo) {
                              return (
                                <TooltipProvider key={action.action_id} delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      {actionBlock}
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="max-w-xs bg-red-600 text-white border-red-700"
                                    >
                                      <p className="font-semibold text-xs mb-1">📋 Information importante</p>
                                      <p className="text-xs whitespace-pre-wrap">{action.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }

                            return actionBlock;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Liaisons Legend & List */}
      {visibleLiaisons.length > 0 && (
        <Card className="border-2 rounded-sm">
          <CardHeader className="pb-4 border-b-2 border-border">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-primary" />
                Liaisons ({visibleLiaisons.length})
              </CardTitle>
              <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-0.5 bg-blue-500" />
                  <span className="text-muted-foreground">Séquence</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-0.5 bg-orange-500" />
                  <span className="text-muted-foreground">Dépendance</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-0.5 bg-green-500 border-dashed" style={{ borderTopWidth: 2, borderStyle: 'dashed' }} />
                  <span className="text-muted-foreground">Parallèle</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-2">
              {visibleLiaisons.map((liaison) => (
                <div
                  key={liaison.liaison_id}
                  className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm"
                  data-testid={`liaison-item-${liaison.liaison_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge 
                      variant="outline" 
                      className="font-mono text-xs"
                      style={{ borderColor: liaisonTypeColors[liaison.type], color: liaisonTypeColors[liaison.type] }}
                    >
                      {liaison.type}
                    </Badge>
                    <span className="text-sm font-medium">{getActionTitle(liaison.source_action_id)}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{getActionTitle(liaison.target_action_id)}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteLiaison(liaison.liaison_id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-liaison-${liaison.liaison_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="rounded-sm border-2 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading font-semibold text-lg uppercase tracking-tight">
              {editingAction ? 'Modifier l\'action' : 'Nouvelle action'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAction}>
            <div className="grid grid-cols-3 gap-4 py-2">
              {/* Colonne 1: Machine, Titre, Description */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="industrial-label text-xs">Machine</Label>
                  <Select
                    value={actionForm.machine_id}
                    onValueChange={(value) => setActionForm({ ...actionForm, machine_id: value })}
                  >
                    <SelectTrigger className="rounded-sm border-2 h-8" data-testid="action-machine-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {machines.map((machine) => (
                        <SelectItem key={machine.machine_id} value={machine.machine_id}>
                          {machine.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="display_title"
                      checked={actionForm.display_title}
                      onCheckedChange={(checked) => setActionForm({ ...actionForm, display_title: checked })}
                      data-testid="display-title-checkbox"
                      className="h-3 w-3"
                    />
                    <Label htmlFor="display_title" className="industrial-label cursor-pointer text-xs">Titre</Label>
                  </div>
                  <Input
                    value={actionForm.title}
                    onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })}
                    placeholder="Usinage pièce A"
                    className="rounded-sm border-2 h-8"
                    required
                    data-testid="action-title-input"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="display_description"
                        checked={actionForm.display_description}
                        onCheckedChange={(checked) => setActionForm({ ...actionForm, display_description: checked })}
                        data-testid="display-description-checkbox"
                        className="h-3 w-3"
                      />
                      <Label htmlFor="display_description" className="industrial-label cursor-pointer text-xs">Description</Label>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleGenerateDescription}
                      disabled={generatingDescription}
                      className="h-6 text-[10px] gap-1 px-2"
                      data-testid="generate-description-btn"
                    >
                      <Sparkles className="w-3 h-3" />
                      {generatingDescription ? '...' : 'IA'}
                    </Button>
                  </div>
                  <Textarea
                    value={actionForm.description}
                    onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                    placeholder="Description..."
                    className="rounded-sm border-2 min-h-16 text-sm"
                    data-testid="action-description-input"
                  />
                </div>
              </div>

              {/* Colonne 2: Horaires, Ligne, Couleur */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="display_time"
                      checked={actionForm.display_time}
                      onCheckedChange={(checked) => setActionForm({ ...actionForm, display_time: checked })}
                      data-testid="display-time-checkbox"
                      className="h-3 w-3"
                    />
                    <Label htmlFor="display_time" className="industrial-label cursor-pointer text-xs">Horaires</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="datetime-local"
                      value={actionForm.start_time}
                      onChange={(e) => setActionForm({ ...actionForm, start_time: e.target.value })}
                      className="rounded-sm border-2 h-8 text-xs"
                      required
                      data-testid="action-start-input"
                    />
                    <Input
                      type="datetime-local"
                      value={actionForm.end_time}
                      onChange={(e) => setActionForm({ ...actionForm, end_time: e.target.value })}
                      className="rounded-sm border-2 h-8 text-xs"
                      required
                      data-testid="action-end-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="industrial-label text-xs">Ligne</Label>
                    <Input
                      type="number"
                      value={actionForm.line_order}
                      onChange={(e) => setActionForm({ ...actionForm, line_order: parseInt(e.target.value) || 0 })}
                      min={0}
                      className="rounded-sm border-2 h-8"
                      data-testid="action-line-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="industrial-label text-xs">Couleur</Label>
                    <Select
                      value={actionForm.color}
                      onValueChange={(value) => setActionForm({ ...actionForm, color: value })}
                    >
                      <SelectTrigger className="rounded-sm border-2 h-8" data-testid="action-color-select">
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableActionColors.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-3 rounded ${color.bg}`} />
                              <span className="text-xs">{color.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* OF et Article */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="display_of"
                        checked={actionForm.display_of}
                        onCheckedChange={(checked) => setActionForm({ ...actionForm, display_of: checked })}
                        className="h-3 w-3"
                      />
                      <Label htmlFor="display_of" className="industrial-label cursor-pointer text-[10px]">OF</Label>
                    </div>
                    <Input
                      type="text"
                      value={actionForm.of_number}
                      onChange={(e) => setActionForm({ ...actionForm, of_number: e.target.value })}
                      placeholder="OF-2026-001"
                      className="rounded-sm border-2 h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="display_article"
                        checked={actionForm.display_article}
                        onCheckedChange={(checked) => setActionForm({ ...actionForm, display_article: checked })}
                        className="h-3 w-3"
                      />
                      <Label htmlFor="display_article" className="industrial-label cursor-pointer text-[10px]">Article</Label>
                    </div>
                    <Input
                      type="text"
                      value={actionForm.article}
                      onChange={(e) => setActionForm({ ...actionForm, article: e.target.value })}
                      placeholder="ART-12345"
                      className="rounded-sm border-2 h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Colonne 3: Production */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Checkbox
                      id="display_designation"
                      checked={actionForm.display_designation}
                      onCheckedChange={(checked) => setActionForm({ ...actionForm, display_designation: checked })}
                      className="h-3 w-3"
                    />
                    <Label htmlFor="display_designation" className="industrial-label cursor-pointer text-[10px]">Désignation</Label>
                  </div>
                  <Input
                    type="text"
                    value={actionForm.designation}
                    onChange={(e) => setActionForm({ ...actionForm, designation: e.target.value })}
                    placeholder="Pièce mécanique XYZ"
                    className="rounded-sm border-2 h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Checkbox
                      id="display_client"
                      checked={actionForm.display_client}
                      onCheckedChange={(checked) => setActionForm({ ...actionForm, display_client: checked })}
                      className="h-3 w-3"
                    />
                    <Label htmlFor="display_client" className="industrial-label cursor-pointer text-[10px]">Client</Label>
                  </div>
                  <Input
                    type="text"
                    value={actionForm.client}
                    onChange={(e) => setActionForm({ ...actionForm, client: e.target.value })}
                    placeholder="ACME Corp"
                    className="rounded-sm border-2 h-8 text-xs"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="display_order_number"
                        checked={actionForm.display_order_number}
                        onCheckedChange={(checked) => setActionForm({ ...actionForm, display_order_number: checked })}
                        className="h-3 w-3"
                      />
                      <Label htmlFor="display_order_number" className="industrial-label cursor-pointer text-[10px]">N° Cmd</Label>
                    </div>
                    <Input
                      type="text"
                      value={actionForm.order_number}
                      onChange={(e) => setActionForm({ ...actionForm, order_number: e.target.value })}
                      placeholder="CMD-0042"
                      className="rounded-sm border-2 h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="display_quantity"
                        checked={actionForm.display_quantity}
                        onCheckedChange={(checked) => setActionForm({ ...actionForm, display_quantity: checked })}
                        className="h-3 w-3"
                      />
                      <Label htmlFor="display_quantity" className="industrial-label cursor-pointer text-[10px]">Quantité</Label>
                    </div>
                    <Input
                      type="number"
                      value={actionForm.quantity}
                      onChange={(e) => setActionForm({ ...actionForm, quantity: parseInt(e.target.value) || 0 })}
                      min={0}
                      placeholder="100"
                      className="rounded-sm border-2 h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Operator Tracking Section - only shows when editing */}
            {editingAction && (
              <div className="border-2 border-primary/30 rounded-sm p-3 bg-primary/5 mt-2">
                <p className="text-xs font-mono uppercase tracking-wider text-primary font-semibold mb-3">
                  Suivi Opérateur
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {/* Info Section - Read Only */}
                  <div className="space-y-2 bg-background/80 p-2 rounded-sm border">
                    <p className="text-[10px] font-mono uppercase text-muted-foreground">Infos Action</p>
                    <div className="text-xs space-y-1">
                      <div><span className="text-muted-foreground">Machine:</span> <span className="font-medium">{machines.find(m => m.machine_id === actionForm.machine_id)?.name || '-'}</span></div>
                      <div><span className="text-muted-foreground">Tps prévu:</span> <span className="font-medium">{Math.round((new Date(actionForm.end_time) - new Date(actionForm.start_time)) / 60000)} min</span></div>
                      <div><span className="text-muted-foreground">OF:</span> <span className="font-medium">{actionForm.of_number || '-'}</span></div>
                      <div><span className="text-muted-foreground">Article:</span> <span className="font-medium">{actionForm.article || '-'}</span></div>
                    </div>
                  </div>

                  {/* Operator Inputs */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase">Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal rounded-sm border-2 h-8 text-xs"
                            >
                              <CalendarIcon className="mr-2 h-3 w-3" />
                              {actionForm.operator_date ? (
                                format(new Date(actionForm.operator_date), 'dd/MM/yyyy HH:mm', { locale: fr })
                              ) : (
                                <span className="text-muted-foreground">Sélectionner...</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={actionForm.operator_date ? new Date(actionForm.operator_date) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  // Keep the current time or use now
                                  const currentTime = actionForm.operator_date
                                    ? new Date(actionForm.operator_date)
                                    : new Date();
                                  date.setHours(currentTime.getHours(), currentTime.getMinutes());
                                  setActionForm({
                                    ...actionForm,
                                    operator_date: date.toISOString().slice(0, 16)
                                  });
                                }
                              }}
                              locale={fr}
                              initialFocus
                            />
                            <div className="p-3 border-t">
                              <Label className="text-[10px] font-mono uppercase mb-1 block">Heure</Label>
                              <Input
                                type="time"
                                value={actionForm.operator_date ? actionForm.operator_date.slice(11, 16) : ''}
                                onChange={(e) => {
                                  const currentDate = actionForm.operator_date
                                    ? new Date(actionForm.operator_date)
                                    : new Date();
                                  const [hours, minutes] = e.target.value.split(':');
                                  currentDate.setHours(parseInt(hours), parseInt(minutes));
                                  setActionForm({
                                    ...actionForm,
                                    operator_date: currentDate.toISOString().slice(0, 16)
                                  });
                                }}
                                className="rounded-sm border-2 h-8 text-xs"
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase">Opérateur</Label>
                        <Select
                          value={actionForm.operator_name}
                          onValueChange={(value) => setActionForm({ ...actionForm, operator_name: value })}
                        >
                          <SelectTrigger className="rounded-sm border-2 h-8 text-xs">
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {operators.length === 0 ? (
                              <SelectItem value="" disabled>Aucun opérateur</SelectItem>
                            ) : (
                              operators.map((op) => (
                                <SelectItem key={op.operator_id} value={op.name}>
                                  <span className="font-mono text-xs mr-2">{op.code}</span>
                                  {op.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase">Qté OK</Label>
                        <Input
                          type="number"
                          value={actionForm.quantity_produced}
                          onChange={(e) => setActionForm({ ...actionForm, quantity_produced: parseInt(e.target.value) || 0 })}
                          min={0}
                          className="rounded-sm border-2 h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase">Qté NC</Label>
                        <Input
                          type="number"
                          value={actionForm.quantity_nc}
                          onChange={(e) => setActionForm({ ...actionForm, quantity_nc: parseInt(e.target.value) || 0 })}
                          min={0}
                          className="rounded-sm border-2 h-8 text-xs text-destructive"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase">Tps (min)</Label>
                        <Input
                          type="number"
                          value={actionForm.time_spent}
                          onChange={(e) => setActionForm({ ...actionForm, time_spent: parseInt(e.target.value) || 0 })}
                          min={0}
                          className="rounded-sm border-2 h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-1">
                    <Label className="text-[10px] font-mono uppercase">Notes opérateur</Label>
                    <Textarea
                      value={actionForm.operator_notes}
                      onChange={(e) => setActionForm({ ...actionForm, operator_notes: e.target.value })}
                      placeholder="Remarques, problèmes rencontrés..."
                      className="rounded-sm border-2 h-20 text-xs resize-none"
                    />
                  </div>
                </div>

                {/* Completion Toggle */}
                <div className="mt-3 pt-3 border-t border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-5 h-5 ${actionForm.is_completed ? 'text-green-500' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${actionForm.is_completed ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {actionForm.is_completed ? 'Action terminée' : 'Action en cours'}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant={actionForm.is_completed ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => setActionForm({ ...actionForm, is_completed: !actionForm.is_completed })}
                    className={`rounded-sm gap-2 ${actionForm.is_completed ? 'border-green-500 text-green-600 hover:bg-green-50' : 'bg-green-600 hover:bg-green-700'}`}
                    data-testid="toggle-completed-btn"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {actionForm.is_completed ? 'Rouvrir' : 'Terminé'}
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2 flex-wrap pt-3 border-t mt-2">
              {editingAction && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { handleDuplicateAction(editingAction.action_id); setActionDialogOpen(false); }}
                    className="rounded-sm border-2"
                    data-testid="duplicate-action-dialog-btn"
                  >
                    <CopyPlus className="w-4 h-4 mr-2" />
                    Dupliquer
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleGenerateNotifications(editingAction.action_id)}
                    className="rounded-sm border-2"
                    data-testid="notify-action-dialog-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Rappel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteAction}
                    className="rounded-sm"
                    data-testid="delete-action-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </>
              )}
              <Button type="button" variant="outline" onClick={() => setActionDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm" data-testid="save-action-btn">
                {editingAction ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Liaison Dialog */}
      <Dialog open={liaisonDialogOpen} onOpenChange={setLiaisonDialogOpen}>
        <DialogContent className="rounded-sm border-2">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
              Nouvelle liaison
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-wider">
              Créez un lien entre deux actions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitLiaison}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="industrial-label">Action source</Label>
                <Select
                  value={liaisonForm.source_action_id}
                  onValueChange={(value) => setLiaisonForm({ ...liaisonForm, source_action_id: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="liaison-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredActions.map((action) => (
                      <SelectItem key={action.action_id} value={action.action_id}>
                        {action.title} ({getMachineName(action.machine_id)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Action cible</Label>
                <Select
                  value={liaisonForm.target_action_id}
                  onValueChange={(value) => setLiaisonForm({ ...liaisonForm, target_action_id: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="liaison-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredActions.map((action) => (
                      <SelectItem key={action.action_id} value={action.action_id}>
                        {action.title} ({getMachineName(action.machine_id)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Type de liaison</Label>
                <Select
                  value={liaisonForm.type}
                  onValueChange={(value) => setLiaisonForm({ ...liaisonForm, type: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="liaison-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sequence">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-0.5 bg-blue-500" />
                        Séquence (A puis B)
                      </div>
                    </SelectItem>
                    <SelectItem value="dependency">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-0.5 bg-orange-500" />
                        Dépendance (B dépend de A)
                      </div>
                    </SelectItem>
                    <SelectItem value="parallel">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-0.5 bg-green-500 border-dashed" style={{ borderStyle: 'dashed' }} />
                        Parallèle (A et B simultanés)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLiaisonDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm" data-testid="save-liaison-btn">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="rounded-sm border-2">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
              {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-wider">
              {editingTemplate ? 'Modifiez le template d\'action' : 'Créez un template d\'action réutilisable'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitTemplate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="industrial-label">Nom du template</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="Usinage Standard"
                  className="rounded-sm border-2"
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Titre de l'action</Label>
                <Input
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                  placeholder="Usinage pièce"
                  className="rounded-sm border-2"
                  required
                  data-testid="template-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Description par défaut</Label>
                <Textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Description de l'action..."
                  className="rounded-sm border-2 min-h-20"
                  data-testid="template-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="industrial-label">Durée (minutes)</Label>
                  <Input
                    type="number"
                    value={templateForm.duration_minutes}
                    onChange={(e) => setTemplateForm({ ...templateForm, duration_minutes: parseInt(e.target.value) || 60 })}
                    min={5}
                    className="rounded-sm border-2"
                    data-testid="template-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="industrial-label">Type de machine</Label>
                  <Select
                    value={templateForm.default_machine_type}
                    onValueChange={(value) => setTemplateForm({ ...templateForm, default_machine_type: value })}
                  >
                    <SelectTrigger className="rounded-sm border-2" data-testid="template-machine-type-select">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aucun">Aucun</SelectItem>
                      <SelectItem value="cnc">CNC</SelectItem>
                      <SelectItem value="tour">Tour</SelectItem>
                      <SelectItem value="fraiseuse">Fraiseuse</SelectItem>
                      <SelectItem value="perceuse">Perceuse</SelectItem>
                      <SelectItem value="rectifieuse">Rectifieuse</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              {editingTemplate && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteTemplate}
                  className="rounded-sm"
                  data-testid="delete-template-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm" data-testid="save-template-btn">
                {editingTemplate ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Drag Ghost - floating action block that follows the cursor */}
      {isDragging && draggingAction && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            left: dragPosition.x,
            top: dragPosition.y,
            width: getActionPosition(draggingAction).width,
            height: ROW_HEIGHT - 8
          }}
        >
          <div
            className={`${getActionColor(draggingAction, filteredActions.indexOf(draggingAction)).bg} text-white rounded-sm shadow-2xl border-2 border-white/50 h-full flex px-2 py-1 opacity-95`}
          >
            <GripVertical className="w-3 h-3 opacity-60 flex-shrink-0 mt-0.5" />
            <div className="flex flex-col justify-center flex-1 px-1 overflow-hidden select-none">
              {draggingAction.display_title !== false && (
                <span className="font-medium text-xs truncate leading-tight">
                  {draggingAction.title}
                </span>
              )}
              {draggingAction.display_time !== false && (
                <span className="text-[10px] opacity-80 truncate leading-tight">
                  {format(parseISO(draggingAction.start_time), 'HH:mm', { locale: fr })} - {format(parseISO(draggingAction.end_time), 'HH:mm', { locale: fr })}
                </span>
              )}
              {draggingAction.display_of && draggingAction.of_number && (
                <span className="text-[10px] opacity-80 truncate leading-tight">OF: {draggingAction.of_number}</span>
              )}
              {draggingAction.display_quantity && draggingAction.quantity > 0 && (
                <span className="text-[10px] opacity-80 truncate leading-tight">Qté: {draggingAction.quantity}</span>
              )}
            </div>
          </div>
          {dragPreviewTime && (
            <div className="mt-1 bg-primary text-primary-foreground text-xs px-3 py-1.5 rounded-sm font-mono shadow-lg border-2 border-white/30">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-3 h-3" />
                <span className="font-bold">
                  {format(dragPreviewTime.start, 'HH:mm', { locale: fr })} - {format(dragPreviewTime.end, 'HH:mm', { locale: fr })}
                </span>
              </div>
              <div className="text-[10px] opacity-80 mt-0.5">
                {format(dragPreviewTime.start, 'EEEE d MMM', { locale: fr })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
